import { useState, useCallback } from 'react';

const GRAPH_API_BASE_URL = "https://graph.microsoft.com/v1.0";
const DOCUMENTS_DRIVE_NAME = "Documents";
const DOCUMENT_LIBRARY_TYPE = "documentLibrary";
const SHARED_DOCUMENTS_NAME = "Shared Documents";
const SHARED_DOCUMENTS_ENCODED = "Shared%20Documents";

export default function useSharePointPdf(acquireToken) {
    const [pdfBlob, setPdfBlob] = useState(null);
    const [pdfUrl, setPdfUrl] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPdfUrl, setCurrentPdfUrl] = useState(null);

    const parseSharePointUrl = async (url, accessToken) => {
        try {
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/');

            const siteIndex = pathParts.indexOf('sites');
            if (siteIndex === -1) {
                return null;
            }

            const siteName = pathParts[siteIndex + 1];
            const siteId = `${urlObj.hostname}:/sites/${siteName}:`;

            const sharedDocsIndex = pathParts.findIndex(part =>
                decodeURIComponent(part) === SHARED_DOCUMENTS_NAME ||
                part === SHARED_DOCUMENTS_ENCODED
            );

            if (sharedDocsIndex === -1) {
                return null;
            }

            const filePathParts = pathParts.slice(sharedDocsIndex + 1);
            const filePath = '/' + filePathParts.map(part => decodeURIComponent(part)).join('/');

            const drivesResponse = await fetch(`${GRAPH_API_BASE_URL}/sites/${siteId}/drives`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            if (!drivesResponse.ok) {
                throw new Error(`Failed to fetch drives: ${drivesResponse.status}`);
            }

            const drivesData = await drivesResponse.json();

            const documentsDrive = drivesData.value.find(drive =>
                drive.name === DOCUMENTS_DRIVE_NAME && drive.driveType === DOCUMENT_LIBRARY_TYPE
            );

            if (!documentsDrive) {
                throw new Error("Could not find Documents drive for this site");
            }

            return {
                siteId,
                driveId: documentsDrive.id,
                filePath
            };
        } catch (err) {
            console.error("Error parsing SharePoint URL:", err);
            return null;
        }
    };

    const fetchPdfFromUrl = useCallback(async (sharePointUrl) => {

        if (loading) {
            return;
        }

        try {
            window.pdfFetchInProgress = false;
            setLoading(true);
            setPdfBlob(null);
            setPdfUrl("");
            setError(null);

            const accessToken = await acquireToken();
            if (!accessToken) {
                throw new Error("Failed to acquire Graph token");
            }

            const urlParts = await parseSharePointUrl(sharePointUrl, accessToken);
            if (!urlParts) {
                throw new Error("Could not parse SharePoint URL");
            }

            const graphPdfUrl = `${GRAPH_API_BASE_URL}/drives/${urlParts.driveId}/root:${urlParts.filePath}:/content`;

            const response = await fetch(graphPdfUrl, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
            }

            const blob = await response.blob();
            setPdfBlob(blob);
            setPdfUrl(URL.createObjectURL(blob));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [acquireToken, loading]);

    const clearPdf = () => {
        setPdfBlob(null);
        setPdfUrl("");
        setError(null);
        setCurrentPdfUrl(null);
    };

    const setPdfClickUrl = useCallback((url) => {
        setCurrentPdfUrl(url);
        setPdfBlob(null);
        setPdfUrl("");
    }, []);

    return {
        pdfBlob,
        pdfUrl,
        loading,
        error,
        currentPdfUrl,
        fetchPdfFromUrl,
        clearPdf,
        setPdfClickUrl
    };
}